import { getConfigSetting } from '@components/configuration/configuration';
import { ConfigName } from '@constants/ConfigName';
import { Grid, Tab, Tabs } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

import { history } from '../../_helpers/history';
import { hasMarketplaceAccessInAnyWorkspace, isVendorUser } from '../../_helpers/MarketplaceAccessHelper';
import { getSuffixOfImageId } from '../../_helpers/ProfilePicHelper';
import auth from '../../_services/auth/Auth';
import BitmapImg from '../../static/_assets/koncert-logo.svg';
import MenuDropDown from '../menu-options/MenuDropDown';
import { hasPermission } from '../permissions/CheckPermission';
import TnAvatar from '../tn-avatar/TnAvatar';

interface IProps extends RouteComponentProps<any> {
  [key: string]: any;
}

interface Tab {
  value: any;
  label: any;
  onclick: (...args: any[]) => any;
}

interface IState {
  tabs: Array<Tab>;
  [key: string]: any;
}

const dashboard: Tab = {
  value: 'clients',
  label: 'Clients',
  onclick: () => {
    history.push('/');
  },
};

const job: Tab = {
  value: 'jobs',
  label: 'Jobs',
  onclick: () => {
    history.push('/jobs');
  },
};

const candidates: Tab = {
  value: 'candidates',
  label: 'Candidates',
  onclick: () => {
    history.push('/candidates');
  },
};

const marketplace: Tab = {
  value: 'marketplace',
  label: 'Marketplace',
  onclick: () => {
    history.push('/marketplace');
  },
};

const vendor: Tab = {
  value: 'vendors',
  label: 'Vendors',
  onclick: () => {
    history.push('/vendors');
  },
};

const profile: Tab = {
  value: 'profile',
  label: 'Profile',
  onclick: () => {
    history.push('/vendorProfile/profile');
  },
};

const settings: Tab = {
  value: 'systemSettings',
  label: 'Admin Settings',
  onclick: () => {
    history.push('/systemSettings/user');
  },
};

class NavigationBar extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      value: localStorage.getItem('currentTab') ? localStorage.getItem('currentTab') : 'dashboard',
      tabs: this.getTabsBasedOnRole(this.props.role),
      isEligibleForMarketplace: false,
      candidateModalEnabled: false,
      isRevampedFiltersEnabled: false,
      isVendorManagementEnabled: false,
      isMarketplaceV2Enabled: false,
      hasJobsAccess: !isVendorUser(),
    };
    this.handleChange = this.handleChange.bind(this);
    this.getMenuItems = this.getMenuItems.bind(this);
  }

  getConfigs = () => {
    getConfigSetting(ConfigName.CANDIDATE_MODAL_ENABLED).then((result) => {
      this.setState({ candidateModalEnabled: result });
    });
    getConfigSetting(ConfigName.REVAMPED_FILTERS_ENABLED).then((result) => {
      this.setState({ isRevampedFiltersEnabled: result });
    });
    getConfigSetting(ConfigName.MARKETPLACE_V2_ENABLED).then((result) => {
      this.setState({ isMarketplaceV2Enabled: result });
    });
    Promise.all([getConfigSetting(ConfigName.VENDOR_MANAGEMENT), getConfigSetting(ConfigName.VENDOR_PROFILE_ENABLED)])
      .then(([isVendorManagementEnabled, isVendorProfileEnabled]) => {
        const tabs = this.getTabsBasedOnRole(this.props.role, isVendorManagementEnabled, isVendorProfileEnabled);
        this.setState({ isVendorManagementEnabled, isVendorProfileEnabled, tabs });
      })
      .catch((error) => {
        console.error('Error fetching config settings:', error);
      });
  };

  componentWillMount() {
    this.getConfigs();
    this.addAdminSettingsTab();
  }

  async componentDidMount() {
    this.setState({ hasJobsAccess: await hasMarketplaceAccessInAnyWorkspace() });
  }

  getMenuItems() {
    const menuItemObj = [
      {
        label: 'Logout',
        onclick: () => {
          history.push('/login');
        },
      },
    ];

    return menuItemObj;
  }

  getCurrentTab() {
    const currentPath = auth.getCurrentRoute();
    if (currentPath.split('/')[1] === '') {
      return 'clients';
    } else if (currentPath.startsWith('/candidates')) {
      return 'candidates';
    } else if (currentPath.startsWith('/marketplace')) {
      return 'marketplace';
    } else if (
      currentPath.startsWith('/vendorOnboarding') ||
      currentPath.startsWith('/vendors') ||
      currentPath.startsWith('/vendorDetails')
    ) {
      return 'vendors';
    } else if (currentPath.startsWith('/vendorProfile')) {
      return 'profile';
    } else if (currentPath.startsWith('/systemSettings')) {
      return 'systemSettings';
    }
    if (currentPath.startsWith('/jobs') || currentPath.startsWith('/createJob')) {
      return 'jobs';
    }
  }

  getTabsBasedOnRole = (role: string, isVendorManagementEnabled?: boolean, isVendorProfileEnabled?: boolean) => {
    const tabs = [dashboard];
    switch (role.toLocaleUpperCase()) {
      case 'MSP':
        tabs.push(job, candidates);
        if (isVendorManagementEnabled) {
          tabs.push(vendor);
        }
        break;
      case 'VENDOR':
        tabs.push(marketplace, job);
        if (isVendorProfileEnabled) {
          tabs.push(profile);
        }
        break;
    }

    return tabs;
  };

  handleChange(value: any) {
    localStorage.setItem('currentTab', value);
    this.setState({ value: value });
  }

  handleAvatarClick(event: any) {
    const { currentTarget } = event;
    this.setState((state) => ({
      openMenu: !state.openMenu,
      event: currentTarget,
    }));
  }

  handleClose() {
    this.setState({ openMenu: false });
  }

  async addAdminSettingsTab() {
    const hasSuperAdminSettings = await hasPermission('super_admin_settings');
    if (this.state.isMarketplaceV2Enabled || hasSuperAdminSettings) {
      hasPermission('system_settings')
        .then((hasSystemSettingsPermission) => {
          if (hasSystemSettingsPermission) {
            this.setState((prevState) => {
              const currentTabs = [...prevState.tabs];
              currentTabs.push(settings);

              return { tabs: currentTabs };
            });
          }
        })
        .catch((error: string) => {
          console.error('Error: ', error);
        });
    }
  }

  render() {
    const currentTab = this.getCurrentTab();
    const { classes, userName, role, imageId } = this.props;
    const { tabs, hasJobsAccess: showJobsTab } = this.state;
    const visibleTabs = tabs?.filter((tab) => showJobsTab || tab.value !== job.value);

    return (
      <Grid
        container
        className={clsx(
          classes.base,
          this.state.isRevampedFiltersEnabled ? classes.navBarBorder : classes.navBoxShadow,
        )}
        justify="center"
      >
        <Grid item xs={11} className={classes.navbarContainer}>
          <div className={classes.headerTopRow}>
            <div className={classes.koncertAvatarGrid}>
              <img
                alt="home-page"
                onClick={() => history.push('/')}
                src={BitmapImg}
                className={classes.koncertAvatar}
                data-testid={'KoncertIt_Logo'}
              />
            </div>
            <div className={classes.upperMenu}>
              <Grid container>
                <Grid item xs={12}>
                  <Tabs
                    value={currentTab}
                    classes={{
                      root: classes.tabsRoot,
                      indicator: classes.tabsIndicator,
                      fixed: classes.scrollableFixed,
                    }}
                  >
                    {visibleTabs?.map((tab, index: number) => {
                      return (
                        <MyTab
                          value={tab.value}
                          label={tab.label}
                          key={tab.label + index}
                          onClick={() => {
                            this.handleChange(tab.value);
                            tab.onclick();
                          }}
                          data-testid={tab.label}
                        />
                      );
                    })}
                  </Tabs>
                </Grid>
              </Grid>
            </div>
            <div className={classes.flexGrow} />
            <div className={classes.flexRow}>
              <div className={classes.flexRow}>
                <TnAvatar src={getSuffixOfImageId(imageId)} name={userName} />
                <IconButton
                  style={{ padding: 0, color: 'black' }}
                  aria-label="Delete"
                  color="primary"
                  onClick={(event) => this.handleAvatarClick(event)}
                  data-testid={'Navbar_Menu_Dropdown'}
                >
                  <ExpandMore />
                </IconButton>
                <MenuDropDown
                  open={this.state.openMenu}
                  userName={userName}
                  role={role}
                  showUser={true}
                  avatarUrl={getSuffixOfImageId(imageId)}
                  event={this.state.event}
                  handleClose={() => this.handleClose()}
                  menuItems={this.getMenuItems()}
                />
              </div>
            </div>
          </div>
          <Grid container className={classes.lowerMenu}>
            <Grid item xs={12}>
              <Tabs
                value={currentTab}
                classes={{
                  root: classes.tabsRoot,
                  indicator: classes.tabsIndicator,
                  fixed: classes.scrollableFixed,
                }}
              >
                {visibleTabs?.map((tab, index: number) => {
                  return (
                    <MyTab
                      value={tab.value}
                      label={tab.label}
                      key={tab.label + index}
                      onClick={() => {
                        this.handleChange(tab.value);
                        tab.onclick();
                      }}
                      data-testid={tab.label}
                    />
                  );
                })}
              </Tabs>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default withStyles((theme) => ({
  base: {
    height: 61,
    width: '100%',
    backgroundColor: 'white',
    position: 'fixed',
    zIndex: 10005,
  },
  navbarContainer: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 1920,
    },
  },
  navBarBorder: {
    borderBottom: '1px solid #eeeeee',
  },
  navBoxShadow: {
    boxShadow: '0px 2px 9px rgba(0, 0, 0, 0.1)',
  },
  flexGrow: {
    flexGrow: 1,
  },
  tabsRoot: {
    borderBottom: '0px solid #e8e8e8',
  },
  tabsIndicator: {
    backgroundColor: 'unset',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  koncertAvatarGrid: {
    marginRight: '20px',
  },
  koncertAvatar: {
    width: 30,
    cursor: 'pointer',
    padding: '1.305% 0px 0px 0px',
    display: 'block',
  },
  upperMenu: {
    marginLeft: 20,
    ['@media (max-width:700px)']: {
      // eslint-disable-line no-useless-computed-key
      display: 'none',
    },
  },
  lowerMenu: {
    display: 'none',
    ['@media (max-width:700px)']: {
      // eslint-disable-line no-useless-computed-key
      display: 'block',
    },
  },
  profileName: {
    height: 16,
    width: 120,
    color: '#000000',
    display: 'flex',
    justifyContent: 'center',
  },
  headerTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
}))(withRouter(NavigationBar));

const MyTab = withStyles((theme) => ({
  root: {
    textTransform: 'initial',
    minWidth: 10,
    paddingLeft: 0,
    height: 59,
    fontSize: '14px',
    fontWeight: 10,
    color: 'rgba(26, 22, 23, 0.92)',
    opacity: 1,
    marginRight: 40,
    '&:last-child': {
      paddingRight: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    borderBottom: '3px solid #ffffff',
    borderImage: `linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.secondary.main}) 1 stretch`,
  },
  wrapper: {
    padding: 'unset',
    fontFamily: 'Avenir medium',
  },
  label: {
    height: 20,
    width: 66,
    fontSize: 16,
    lineHeight: 'unset',
  },
  indicator: {
    display: 'none',
  },
  button: {
    padding: '0px',
  },
}))(Tab);
